import React from 'react';
import type { UnitType } from 'dayjs';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import { sha256 } from 'js-sha256';
import UAParser from 'ua-parser-js';
import { BROWSER, OS, STORAGE_KEYS, REG_EX } from './constants';
import { LooseObjectType, PhoneNumberDelimiterType } from './globalTypes';

dayjs.extend(duration);
dayjs.extend(relativeTime);

/**
 * Function to parse the json data
 */
export function parseJSON(
  data: string | null | undefined,
  defaultValue: unknown,
) {
  return JSON.parse(data || JSON.stringify(defaultValue));
}

/**
 * Function to store the value in local storage under specific key
 */

export function setInStorage(key: string, value: unknown) {
  sessionStorage.setItem(key, JSON.stringify(value));
}

/**
 * Function to get the specific key value from the local storage
 */

export function getFromStorage(key: string, defaultValue: unknown = '') {
  return parseJSON(sessionStorage.getItem(key), defaultValue);
}

/**
 * Function to romove the specific key from the local storage
 */
export function removeFromStorage(key: string) {
  sessionStorage.removeItem(key);
}

/**
 * Function to read access token from persist store
 */
export function getAccessToken(): string | null {
  const sessionData = getFromStorage(STORAGE_KEYS.persistRoot, {})?.session;
  if (sessionData) {
    return parseJSON(sessionData, {}).accessToken;
  }
  return null;
}

/**
 * Function to mask the string upto the last specified charecters with the specified character
 */

export function maskify(str: string, maskChar = '•', length = 4) {
  const pattern = `.(?=.{${length}})`;
  const regExpression = new RegExp(pattern, 'g');
  return str.replace(regExpression, maskChar);
}

/**
 * Function will take any string formatted text and convert it to phone number formatted string with the delimiter character
 */

export function formatAsPhoneNumber(
  str: string,
  delimiter: PhoneNumberDelimiterType = '-',
) {
  function phoneNumber(str2: string) {
    return (
      str2.slice(0, 3) +
      delimiter +
      str2.slice(3, 6) +
      delimiter +
      str2.slice(6)
    );
  }

  if (!str) {
    return '';
  }

  if (delimiter === '(xxx) xxx-xxxx') {
    return `(${str.slice(0, 3)}) ${str.slice(3, 6)}-${str.slice(6)}`;
  }

  if (str.length === 11) {
    return str.slice(0, 1) + delimiter + phoneNumber(str.slice(1));
  }

  return phoneNumber(str);
}

/**
 * Function to convert as the phone number formatted string by removing all non digit characters
 *
 * return (123) 456-7890 or 123-456-7890
 */
export function formatPhoneNumber(
  phoneNumberString: string,
  delimiter: PhoneNumberDelimiterType = '-',
) {
  const cleaned = phoneNumberString.replace(/\D/g, '');
  return cleaned ? formatAsPhoneNumber(cleaned, delimiter) : null;
}

/**
 * Function to check the input date is valid or not
 */
export function isValidDate(date: string | undefined | null) {
  if (!date) {
    return false;
  }

  return dayjs(date).isValid();
}

/**
 * Function to get date in specified format
 */
export function formatDate(
  date: string | undefined | null,
  format = 'MM/DD/YYYY',
): string {
  if (!date) {
    return 'NA';
  }

  return dayjs(date).isValid() ? dayjs(date).format(format) : 'NA';
}

/* Function to format the date from timestamp */
export function formatDateTimeStamp(
  timestamp: number,
  format = 'MMM DD, YYYY',
) {
  if (!timestamp) {
    return '-';
  }

  const date =
    timestamp.toString().length === 10
      ? dayjs.unix(timestamp)
      : dayjs(timestamp);
  return date.isValid() ? date.format(format) : '-';
}

/**
 * Function to get the humanize format date
 */
export function humanizeDate(data: {
  date: string | null;
  refDate?: string;
  unit?: Exclude<UnitType, 'date' | 'dates'>;
}) {
  const { date, refDate, unit = 'days' } = data;
  const date1 = dayjs(date);
  const date2 = refDate ? dayjs(refDate) : dayjs();

  if (!date1.isValid() || !date2.isValid()) {
    return null;
  }

  return dayjs.duration(date1.diff(date2, unit), unit).humanize(true);
}

/**
 * Function to return the sha256 hash in hex string
 */
export function getHashInHex(message: string) {
  if (!message) {
    return message;
  }

  return sha256.hex(message);
}

/**
 * Function to get the last specified charecters of a string
 */
export function getLastChars(str: string, chars = 4) {
  if (!str) {
    return str;
  }

  return str.slice(-chars);
}

export function getOS(): string {
  const parser = new UAParser();

  const osName = parser.getResult().os.name;
  if (osName) {
    if (/Mac OS/.test(osName)) {
      return OS.mac;
    }
    if (/Windows/.test(osName)) {
      return OS.windows;
    }
    if (/iOS/.test(osName)) {
      return OS.ios;
    }
    if (/Android/.test(osName)) {
      return OS.android;
    }
  }
  return OS.other;
}

export function getBrowser(): string {
  const parser = new UAParser();
  const browserName = parser.getResult().browser.name;
  switch (browserName) {
    case BROWSER.chrome:
      return BROWSER.chrome;
    case BROWSER.msEdge:
      return BROWSER.msEdge;
    case BROWSER.firefox:
      return BROWSER.firefox;
    case BROWSER.safari:
      return BROWSER.safari;
    default:
      return BROWSER.other;
  }
}

export function getDevice() {
  const parser = new UAParser();
  return parser.getResult().device.type;
}

// to get Password Strength Indicator
export function getPasswordStrength(type: number) {
  const strengthIndicators = ['Bad', 'Weak', 'Good', 'Strong', 'Excellent'];
  return strengthIndicators[type] ?? 'Unknown';
}

/* Get file extension from file name */
export function getFileExtension(filename: string) {
  return filename.split('.').pop() ?? filename;
}

/* Truncate Text using ellipsis */
export function truncateText(text: string, maxLength = 20) {
  if (text.length > maxLength) {
    return `${text.slice(0, maxLength)}...`;
  }
  return text;
}

/* Captitalize the first character of every word in a string */
export function capitalize(str: string) {
  if (!str) {
    return str;
  }

  return str
    .split(' ')
    .map((word) => word[0].toUpperCase() + word.substring(1).toLowerCase())
    .join(' ');
}

/* Function to get the base64 url of a file */
export function getBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}

/* Function to download the base64 url */
export function downloadBase64Url(base64Url: string, fileName: string) {
  const link = document.createElement('a');
  link.href =
    base64Url.indexOf('base64,') === -1
      ? `data:application/octet-stream;base64,${base64Url}`
      : base64Url;
  link.download = fileName || 'downloaded-file';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/* Function converting Unicode to Character */
export function ConvertUnicodeToCharacter(text: string) {
  const decoded = text.replace(/&#x([0-9a-f]+);/gi, (_, code) =>
    String.fromCharCode(parseInt(code, 16)),
  );
  return decoded;
}

export function buildAddress(address: LooseObjectType) {
  return `${address?.street_line} ${address?.secondary} ${address?.city}, ${address?.state} ${address?.zipcode}`;
}

export function addSearchParams(url: string, params: LooseObjectType = {}) {
  const urlObj = new URL(url);

  return new URL(
    `${urlObj.origin}${urlObj.pathname}?${new URLSearchParams([
      ...Array.from(urlObj.searchParams.entries()),
      ...Object.entries(params),
    ])}`,
  ).toString();
}

export function addCarrierIdSearchParam(url: string, carrierId: string) {
  return addSearchParams(url, {
    carrier_id: carrierId,
  });
}

export function formatFileSize(bytes: number) {
  if (bytes === 0) return '0 Bytes';

  const units = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));

  const size = (bytes / 1024 ** i).toFixed(1);

  return `${
    parseFloat(size) === Math.floor(parseFloat(size))
      ? Math.floor(parseFloat(size))
      : size
  } ${units[i]}`;
}

export function numericValuesAllowed(e: React.KeyboardEvent<HTMLDivElement>) {
  if ('key' in e) {
    const { key, ctrlKey } = e;
    const allowedKeys = new Set([
      'Backspace',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Delete',
      'Enter',
    ]);

    if (!/^\d$/.test(key) && !ctrlKey && !allowedKeys.has(key)) {
      e.preventDefault();
    }
  }
}

export function pasteNumericValuesAllowed(
  e: React.ClipboardEvent<HTMLDivElement>,
) {
  if ('clipboardData' in e) {
    const pastedText = e.clipboardData.getData('text');
    if (!/^\d+$/.test(pastedText)) {
      e.preventDefault();
    }
  }
}

export function currencyFormat(
  price: number | undefined | null,
  currency = 'USD',
  locale = 'en-US',
) {
  const value = price ? +price / 100 : 0;

  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(
    value,
  );
}

// copy to clipboard
export async function doCopyToClipboard(data: string) {
  try {
    await navigator.clipboard.writeText(data);
    return true;
  } catch (error) {
    return Promise.reject(error);
  }
}

export function logErrors(error: unknown) {
  console.error('Error logs::', error);
}

/* Function to check the value is boolean or not */
export function isBoolean(val: boolean | undefined | null) {
  if (typeof val === 'boolean') {
    return true;
  }

  return false;
}

/* Function to check the provided value is valid domain URL or not */
export function isValidDomainUrl(url: string | null | undefined) {
  if (!url) {
    return false;
  }

  return REG_EX.websiteDomain.test(url);
}

/* Function to check the provided value is valid email address or not */
export function isValidEmail(val: string | null | undefined) {
  if (!val) {
    return false;
  }

  return REG_EX.email.test(val);
}
